import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Star } from "lucide-react";

const reviews = [
  {
    name: "Alberto Hidalgo",
    rating: 5,
    text: "Excelentes profesionales que ponen todo su talento y pasión en cada trabajo por muy grande o pequeño que sea. Son formidables personas que viven de lo que más les gusta hacer. Si se busca un trabajo excelente son la mejor opción.",
    date: "Hace 3 años",
  },
  {
    name: "M",
    rating: 5,
    text: "Desde el principio, entendieron perfectamente lo que queríamos. El equipo es súper profesional y creativo, y siempre estuvieron abiertos a nuestras ideas. El resultado final fue impresionante. Además, siempre fueron muy claros con todo y respetaron los tiempos y el presupuesto. Son gente sería que sabe cómo hacer un gran trabajo.",
    date: "Hace 2 años",
  },
  {
    name: "Fk",
    rating: 5,
    text: "Gracias, gracias, gracias. Hemos tenido un lanzamiento por todo lo alto. Gracias Galvani, por vuestro asesoramiento en todo el proceso y sobre todo por el resultado final. Nos vemos el próximo año!",
    date: "Hace 2 años",
  },
  {
    name: "Rafael Mo",
    rating: 5,
    text: "¡Excelente productora audiovisual! Desde el primer momento la atención fue muy profesional, serios y atentos a los pequeños detalles, eficaces, dispuestos a escuchar y aportar nuevas ideas. El resultado final fue mejor de lo que esperaba, tanto en reportajes como en video corporativo. Sin duda, os recomiendo trabajar con ellos.",
    date: "Hace 3 años",
  },
  {
    name: "Antonio Rubio Ortiz",
    rating: 5,
    text: "Muy profesionales, disponen de muchísima técnica y proporcionan todo lo que necesitas al momento. 10/10 la mejor productora audiovisual del país.",
    date: "Hace 3 años",
  },
  {
    name: "Javier Ep",
    rating: 5,
    text: "Muy profesionales a demás de buena gente, siempre es un placer trabajar con ellos, muy buen rollo, unos cracks.",
    date: "Hace 3 años",
  },
  {
    name: "Nacho Serapio",
    rating: 5,
    text: "Gran estudio con grandes profesionales que se implican al máximo para sacar adelante los proyectos del cliente. Estoy muy contento, y sin lugar a dudas volveré a contar con ellos.",
    date: "Hace 3 años",
  },
  {
    name: "Pablo Eguizabal Zabarte",
    rating: 5,
    text: "Grandes profesionales. Se comprometen al máximo con cada proyecto.",
    date: "Hace 3 años",
  },
  {
    name: "Luis Cámara",
    rating: 5,
    text: "Profesionales del mundo audiovisual como pocos he visto. Serios, resolutivos, creativos, cercanos, complacientes y amables. Totalmente recomendados tanto para ficción, como publicidad o corporativo.",
    date: "Hace 3 años",
  },
  {
    name: "Daniel Martinez",
    rating: 5,
    text: "Auténticos profesionales, da gusto trabajar con gente ellos.",
    date: "Hace 3 años",
  },
];

// Google "G" logo as inline SVG
const GoogleLogo = ({ className = "w-5 h-5" }) => (
  <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// Avatar with gradient based on name initial
const avatarGradients = [
  "from-blue-500 to-cyan-500",
  "from-purple-500 to-pink-500",
  "from-pink-500 to-red-500",
  "from-cyan-500 to-blue-500",
  "from-purple-500 to-blue-500",
  "from-blue-500 to-purple-500",
  "from-pink-500 to-purple-500",
  "from-red-500 to-orange-500",
  "from-blue-500 to-pink-500",
  "from-purple-500 to-cyan-500",
];

const initialOf = (name) => name.trim().charAt(0).toUpperCase();

export default function GoogleReviews() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const totalReviews = reviews.length;
  const avgRating = (reviews.reduce((s, r) => s + r.rating, 0) / totalReviews).toFixed(1);

  return (
    <section
      className="section-spacing bg-white relative overflow-hidden"
      data-testid="google-reviews-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white border border-slate-200 shadow-sm mb-6">
            <GoogleLogo className="w-5 h-5" />
            <span className="text-sm text-galvani-text font-body font-medium">Reseñas verificadas en Google</span>
          </div>

          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-galvani-text mb-6">
            Lo que dicen los clientes que <span className="gradient-text">ya trabajaron con nosotros</span>
          </h2>

          {/* Rating summary */}
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-slate-50 to-blue-50 border border-slate-200">
            <div className="flex items-center gap-3">
              <span className="font-heading font-extrabold text-4xl text-galvani-text">{avgRating}</span>
              <div className="flex flex-col items-start">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-xs text-galvani-textLight font-body">Puntuación media</span>
              </div>
            </div>
            <div className="hidden sm:block w-px h-10 bg-slate-300" />
            <div className="text-center sm:text-left">
              <div className="font-heading font-bold text-xl text-galvani-text">{totalReviews} reseñas</div>
              <span className="text-xs text-galvani-textLight font-body">100% valoraciones de 5 estrellas</span>
            </div>
          </div>
        </motion.div>

        {/* Reviews masonry grid — CSS columns keeps ALL reviews visible with balanced heights */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6"
          data-testid="google-reviews-grid"
        >
          {reviews.map((review, i) => (
            <motion.article
              key={`${review.name}-${i}`}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.05 * i }}
              className="break-inside-avoid p-6 rounded-2xl bg-white border border-slate-200 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              data-testid={`google-review-card-${i}`}
            >
              {/* Top row: avatar + name/date + Google logo */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div
                    className={`w-11 h-11 rounded-full bg-gradient-to-br ${avatarGradients[i % avatarGradients.length]} flex items-center justify-center flex-shrink-0 shadow-sm`}
                  >
                    <span className="font-heading font-bold text-white text-lg">
                      {initialOf(review.name)}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-galvani-text text-sm truncate">
                      {review.name}
                    </h3>
                    <span className="text-xs text-galvani-textLight font-body">{review.date}</span>
                  </div>
                </div>
                <GoogleLogo className="w-4 h-4 flex-shrink-0 mt-1" />
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-3">
                {[...Array(review.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                ))}
              </div>

              {/* Review text */}
              <p className="font-body text-sm text-galvani-text leading-relaxed">
                {review.text}
              </p>
            </motion.article>
          ))}
        </motion.div>

        {/* Trust footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-sm text-galvani-textLight font-body">
            Todas las reseñas son públicas y verificables en la ficha de Google Business Profile de Galvani Studios.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
